'use client';

import { fetchAuthSession } from '@aws-amplify/core';
import {
  changeUserPassword,
  completeNewPasswordChallenge,
  confirmCustomChallengeAnswer,
  confirmPasswordReset,
  requestPasswordReset,
  signInWithPassword,
} from '@/utils/aws/cognito/amplifyAuthLegacy';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  updatePassword,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { Hub } from 'aws-amplify/utils';

export type {
  LegacyAuthChallengeResponse,
  LegacyAuthSignInResult,
  LegacyAuthenticatedUser,
} from '@/utils/aws/cognito/amplifyAuthLegacy';

export { Hub };

export { getCurrentUser, fetchUserAttributes };

export {
  signInWithPassword,
  confirmCustomChallengeAnswer,
  completeNewPasswordChallenge,
  requestPasswordReset,
  confirmPasswordReset,
  changeUserPassword,
};

/** Raw v6 re-exports for advanced/direct use. */
export {
  confirmResetPassword,
  confirmSignIn,
  resendSignUpCode,
  resetPassword,
  signIn,
  signInWithRedirect,
  updatePassword,
};

export async function fetchAuthSessionClient(): ReturnType<
  typeof fetchAuthSession
> {
  return fetchAuthSession();
}

/** Reads the current ID token. Pass forceRefresh only to rotate before expiry. */
export async function getCurrentIdToken(options?: {
  forceRefresh?: boolean;
}): Promise<{
  token: string | null;
  exp: number | null;
}> {
  try {
    // Default fetchAuthSession returns a still-valid JWT unchanged. forceRefresh
    // hits Cognito + PreTokenGeneration — use only from the 120s keep-alive buffer.
    const session = await fetchAuthSession(
      options?.forceRefresh ? { forceRefresh: true } : undefined
    );
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString() ?? null;
    const exp = idToken?.payload?.exp;
    return {
      token,
      exp: typeof exp === 'number' ? exp : null,
    };
  } catch {
    return { token: null, exp: null };
  }
}

export async function signOutUser(): Promise<void> {
  await signOut();
}

/** Clears in-memory / cookie-backed Cognito tokens without requiring an active session. */
export async function clearCognitoTokenStore(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    await cognitoUserPoolsTokenProvider.authTokenStore.clearTokens();
  } catch {
    // Token store may already be empty.
  }
}
